// What the meeting canvas shows between Stop and the finished meeting (PL-30).
//
// Pure helpers over the meeting row the server already returns (status, stage,
// the notes, repair and voice lanes, durations); no new server field. The view
// is meeting-finishing.tsx.
//
//   phase       saving        Stop was pressed; the audio is being uploaded or sealed
//               transcribing  the record pass (words and speaker turns, in parallel)
//               settling      the transcript is saved and the first notes are being
//                             written; the voice and correction lanes may still run
//   steps       transcript, speakers, corrections, notes: done, active or waiting
//   estimate    a typical duration for a meeting this long, never a countdown,
//               measured from a server time so a reload does not restart it
//   tail        the latest lines, so the words being read at Stop stay in view
import type { CompanyMeeting } from './types';

export type FinishingPhase = 'saving' | 'transcribing' | 'settling';
export type FinishingStep = 'transcript' | 'speakers' | 'corrections' | 'notes';
export type FinishingStepState = 'done' | 'active' | 'waiting';
export type FinishingSteps = Record<FinishingStep, FinishingStepState>;

export const FINISHING_STEPS: readonly FinishingStep[] = [
  'transcript',
  'speakers',
  'corrections',
  'notes',
];

type FinishingFields = Pick<
  CompanyMeeting,
  'status' | 'stage' | 'summary' | 'notes' | 'completedAt' | 'repair' | 'voice' | 'durationMs'
> & {
  capture?: CompanyMeeting['capture'];
  captureMethod?: CompanyMeeting['captureMethod'];
  createdAt?: string;
};

/**
 * A ready meeting stays in the finishing view until its first notes exist, so
 * the finished page arrives once instead of re-flowing as notes and names land.
 * Only a recently completed meeting qualifies: an older meeting whose notes are
 * regenerated after an edit is a finished meeting with a notes banner.
 */
export const SETTLE_WINDOW_MS = 30 * 60_000;
const NOTES_BUSY: ReadonlySet<string> = new Set(['generating', 'stale']);
const VOICE_BUSY: ReadonlySet<string> = new Set(['queued', 'running', 'stale']);
const REPAIR_BUSY: ReadonlySet<string> = new Set(['pending', 'running', 'stale']);

export function meetingSettling(meeting: FinishingFields, nowMs: number): boolean {
  if (meeting.status !== 'ready' || meeting.summary) return false;
  if (!NOTES_BUSY.has(meeting.notes?.status ?? 'none')) return false;
  const completed = serverTimeMs(meeting.completedAt);
  return Number.isFinite(completed) && nowMs - completed < SETTLE_WINDOW_MS;
}

/**
 * A server timestamp in ms, or NaN. The API passes Postgres text through
 * ("2026-09-28 08:27:40.283+00"), which some engines (Safari, the iOS shell)
 * do not parse; it is read as the ISO form it stands for.
 */
export function serverTimeMs(value: string | null | undefined): number {
  if (!value) return Number.NaN;
  return Date.parse(value.replace(' ', 'T').replace(/([+-]\d{2})$/u, '$1:00'));
}

/**
 * The finishing phase, or null when the meeting is recording, finished or
 * failed. `stopping`: Stop was pressed on this device or another one while
 * the server still says capturing.
 */
export function finishingPhase(
  meeting: FinishingFields,
  { stopping = false, nowMs }: { stopping?: boolean; nowMs: number },
): FinishingPhase | null {
  if (meeting.status === 'processing')
    return meeting.stage === 'sealing' ? 'saving' : 'transcribing';
  if (meeting.status === 'capturing') return stopping ? 'saving' : null;
  return meetingSettling(meeting, nowMs) ? 'settling' : null;
}

/** Where each step stands. The record pass runs words and speaker turns together. */
export function finishingSteps(meeting: FinishingFields, phase: FinishingPhase): FinishingSteps {
  if (phase !== 'settling') {
    const pass: FinishingStepState = phase === 'transcribing' ? 'active' : 'waiting';
    return { transcript: pass, speakers: pass, corrections: 'waiting', notes: 'waiting' };
  }
  return {
    transcript: 'done',
    speakers: VOICE_BUSY.has(meeting.voice?.status ?? 'none') ? 'active' : 'done',
    corrections: REPAIR_BUSY.has(meeting.repair?.status ?? 'none') ? 'active' : 'done',
    notes: 'active',
  };
}

/** The first step still running, which names the activity line. */
export const activeFinishingStep = (steps: FinishingSteps): FinishingStep | null =>
  FINISHING_STEPS.find((step) => steps[step] === 'active') ?? null;

/** Where the first running step sits (1-based), for the compact progress line. */
export function finishingPosition(steps: FinishingSteps) {
  const step = activeFinishingStep(steps);
  return step ? { step, position: FINISHING_STEPS.indexOf(step) + 1 } : null;
}

// A typical duration, not a promise. The record pass sends the whole recording
// to two engines in parallel, so it scales with the audio; a fixed part covers
// upload, normalisation and the notes. On the lane's measured run a 2-minute
// recording was ready 79 s after upload; the rate below keeps a long meeting's
// estimate on the cautious side. Replace with a server estimate when one exists.
const FIXED_MS = 60_000;
const AUDIO_RATE = 0.25;
const AFTER_TRANSCRIPT_MS = 45_000;

/** How long finishing usually takes from Stop, or null when the length is unknown. */
export function finishingTypicalMs(meeting: FinishingFields, phase: FinishingPhase) {
  if (phase === 'settling') return AFTER_TRANSCRIPT_MS;
  const audioMs = meeting.durationMs ?? meeting.capture?.elapsedMs ?? null;
  if (!audioMs || audioMs <= 0) return null;
  return FIXED_MS + AUDIO_RATE * audioMs + AFTER_TRANSCRIPT_MS;
}

export type FinishingEstimate =
  | { key: 'estimateUnderMinute' }
  | { key: 'estimateMinutes'; minutes: number }
  | { key: 'estimateLonger' };

/**
 * When the wait the typical duration describes began, from what the server
 * says, or NaN when it says nothing usable (the view then counts from when it
 * first saw the meeting). Settling counts from the saved transcript; the rest
 * from Stop. The API returns the Stop time only while capturing or sealing, so
 * `knownStopMs` carries it from an earlier read; after a reload a browser
 * recording's Stop is its start plus the audio, and an import starts when it
 * was added.
 */
export function finishingStartMs(
  meeting: FinishingFields,
  phase: FinishingPhase,
  knownStopMs = Number.NaN,
): number {
  if (phase === 'settling') return serverTimeMs(meeting.completedAt);
  const stop = serverTimeMs(meeting.capture?.stopRequestedAt);
  if (Number.isFinite(stop)) return stop;
  if (Number.isFinite(knownStopMs)) return knownStopMs;
  const created = serverTimeMs(meeting.createdAt);
  if (meeting.captureMethod === 'upload') return created;
  return created + (meeting.durationMs ?? meeting.capture?.elapsedMs ?? Number.NaN);
}

/**
 * The estimate line for a typical duration and the time the wait has run.
 * Past the typical time it says so plainly.
 */
export function finishingEstimate(
  typicalMs: number | null,
  elapsedMs: number,
): FinishingEstimate | null {
  if (typicalMs === null) return null;
  if (elapsedMs > typicalMs) return { key: 'estimateLonger' };
  if (typicalMs <= 60_000) return { key: 'estimateUnderMinute' };
  return { key: 'estimateMinutes', minutes: Math.round(typicalMs / 60_000) };
}

/** A long wait is worth one reassuring line: the creator hears from Inbox. */
export const LONG_WAIT_MS = 3 * 60_000;

// ── Transcript preview ─────────────────────────────────────────────────────

/** One de-emphasised line: a live row while processing, a saved row once settling. */
export type FinishingLine = { key: string; atMs: number; text: string };

/**
 * The latest lines the finishing view shows under its header. Right after
 * Stop the person was reading the newest words (the live panel follows them),
 * so those stay in view with the header; the rest is one disclosure away.
 *
 *   collapsible  offer "Show all lines": more than a narrow canvas's tail
 *   hidden       lines left out on a wide canvas (0 when the transcript fits)
 *   shown        the lines rendered; meeting-finishing.css trims them to the
 *                last TAIL_LINES_COMPACT on a narrow canvas (keep the two in step)
 */
export const TAIL_LINES = 12;
export const TAIL_LINES_COMPACT = 5;
const TAIL_SLACK = 3;

export function finishingTail<T>(lines: readonly T[], expanded: boolean) {
  const collapsible = !expanded && lines.length > TAIL_LINES_COMPACT + TAIL_SLACK;
  const hidden =
    collapsible && lines.length > TAIL_LINES + TAIL_SLACK ? lines.length - TAIL_LINES : 0;
  return { collapsible, hidden, shown: hidden ? lines.slice(hidden) : lines };
}

/** The saved transcript rows as preview lines (settling). */
export function savedTranscriptLines(meeting: Pick<CompanyMeeting, 'segments'>): FinishingLine[] {
  return (meeting.segments ?? []).map((segment) => ({
    key: `segment:${segment.index}`,
    atMs: segment.startMs ?? 0,
    text: segment.displayText,
  }));
}

/**
 * The finished transcript row nearest a preview line's time, so the finished
 * view can open where the person was reading.
 */
export function nearestSegmentIndex(
  meeting: Pick<CompanyMeeting, 'segments'>,
  atMs: number,
): number | null {
  let best: { index: number; distance: number } | null = null;
  for (const segment of meeting.segments ?? []) {
    if (segment.startMs === null) continue;
    const distance = Math.abs(segment.startMs - atMs);
    if (!best || distance < best.distance) best = { index: segment.index, distance };
  }
  return best?.index ?? null;
}
